/**
 * Account → Notifications. Inbox: every notification, by day, filtered, with older ones paged in.
 * Settings: which notifications are pushed. Both carry push on this device (with the iPhone Home
 * Screen and "blocked" cases) and a pointer to the client's own alerts, on Account → Alerts.
 */
import { useCallback, useEffect, useState } from 'react';
import { BRAND, PUSH_GROUPS, type PushPreferences } from '@platform/shared';
import { byDay, inTab } from '../../components/NotificationBell';
import { ctaLabel, Glyph, NotificationIcon, timeLabel } from '../../components/NotificationIcon';
import { SwitchTrack } from '../../components/mobile/chrome';
import { useAddToHomeScreen } from '../../components/mobile/InstallSheet';
import { cx } from '../../components/ui';
import { api } from '../../lib/api';
import { platform } from '../../lib/install';
import { disablePush, enablePush, syncPush, usePushState } from '../../lib/push';
import { Link, navigate } from '../../lib/router';
import { isUrgent, loadOlderNotifications, markAllRead, markRead, openNotification, useHasOlderNotifications, useNotifications, useUnreadCount } from '../../stores/notifications';
import { useMe } from '../../stores/session';
import { Alert, Card, errMsg, H1, H2, Notice, PillButton, Segmented } from './kit';

/** Round grey badge heading a side card, like the Personal information side cards. */
const SideIcon = ({ name }: { name: 'bell' | 'chart' }) => (
  <span aria-hidden="true" className="grid size-12 place-items-center rounded-full bg-track text-fg ring-1 ring-line-strong ring-inset">
    <Glyph name={name} />
  </span>
);

function DeviceCard({ prefs, reload }: { prefs: PushPreferences | null; reload: () => void }) {
  const s = usePushState();
  const me = useMe();
  const install = useAddToHomeScreen();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => {
    void syncPush();
  }, []);

  const on = s.permission === 'granted' && s.subscribed;
  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await fn();
      if (ok) setMsg({ ok: true, text: ok });
      reload();
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e) });
    } finally {
      setBusy(false);
    }
  };

  let body;
  if (me?.signedInBy) {
    body = <p className="m-0 text-muted">Push notifications can’t be turned on in a session opened from the CRM.</p>;
  } else if (prefs && !prefs.available) {
    body = <p className="m-0 text-muted">Push notifications aren’t available yet. You still see every alert under the bell.</p>;
  } else if (s.support === 'ios_needs_install') {
    body = (
      <>
        <Alert tone="info">
          On iPhone and iPad, notifications work in the {BRAND.shortName} app on your Home Screen (iOS 16.4 or later). Add it to your Home Screen, open it from
          there, sign in and turn notifications on.
        </Alert>
        <PillButton onClick={() => void install.start()} className="self-start">
          Add to Home Screen
        </PillButton>
      </>
    );
  } else if (s.support === 'unsupported') {
    body = (
      <p className="m-0 text-muted">
        This browser can’t receive push notifications.{' '}
        {platform() === 'ios' ? 'Update your iPhone to iOS 16.4 or later.' : 'Try Chrome, Edge, Firefox or Safari.'}
      </p>
    );
  } else if (s.permission === 'denied') {
    body = (
      <Alert tone="info">
        Notifications are blocked for {BRAND.shortName} on this device.{' '}
        {platform() === 'ios'
          ? 'Open the iPhone Settings app → Notifications → ' + BRAND.shortName + ' and allow notifications.'
          : 'Allow them in your browser’s site settings (the icon left of the address bar), then come back here.'}
      </Alert>
    );
  } else {
    body = (
      <div className="flex flex-col gap-4">
        <button
          type="button"
          role="switch"
          aria-checked={on}
          disabled={busy}
          onClick={() => void run(on ? disablePush : enablePush)}
          className="flex w-full items-center justify-between gap-4 text-left text-fg disabled:opacity-60"
        >
          <span>
            <span className="block text-[16px] font-semibold">Push notifications on this device</span>
            <span className="block text-[14px] text-muted">{on ? 'On. Alerts reach you even when the app is closed.' : 'Off. You only see alerts under the bell.'}</span>
          </span>
          <SwitchTrack on={on} />
        </button>
        {on && (
          <PillButton kind="secondary" size="sm" busy={busy} className="self-start" onClick={() => void run(() => api.post('/api/push/test'), 'Test notification sent. It should arrive in a few seconds.')}>
            Send a test notification
          </PillButton>
        )}
      </div>
    );
  }
  return (
    <Card className="flex flex-col gap-4">
      <SideIcon name="bell" />
      <H2 className="!text-[22px]">Push on this device</H2>
      {body}
      {prefs && prefs.devices > 0 && (
        <p className="m-0 text-[13px] text-muted">
          Push is on for {prefs.devices} device{prefs.devices > 1 ? 's' : ''} on your account.
        </p>
      )}
      <Notice msg={msg} onClose={() => setMsg(null)} />
      {install.sheet}
    </Card>
  );
}

function CategoriesCard({ prefs, setPrefs }: { prefs: PushPreferences; setPrefs: (p: PushPreferences) => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const toggle = async (key: string, push: boolean) => {
    setBusy(key);
    setMsg(null);
    try {
      setPrefs(await api.put<PushPreferences>('/api/notifications/prefs', { prefs: { [key]: push } }));
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e) });
    } finally {
      setBusy(null);
    }
  };
  return (
    <Card className="flex flex-col gap-5">
      <div>
        <H2>What we notify you about</H2>
        <p className="m-0 mt-1.5 text-[15px] text-muted">Choose what is pushed to your devices. Everything still appears in your inbox.</p>
      </div>
      {PUSH_GROUPS.map((g) => {
        const items = prefs.categories.filter((c) => c.group === g.id);
        if (!items.length) return null;
        return (
          <div key={g.id} className="flex flex-col">
            <h3 className="m-0 mb-1 text-[13px] font-semibold tracking-wide text-muted uppercase">{g.label}</h3>
            {items.map((c) => (
              <button
                key={c.key}
                type="button"
                role="switch"
                aria-checked={c.push}
                disabled={c.mandatory || busy === c.key}
                onClick={() => void toggle(c.key, !c.push)}
                className="flex w-full items-center justify-between gap-4 border-b border-line py-3.5 text-left text-fg last:border-b-0 disabled:cursor-default"
              >
                <span className="min-w-0">
                  <span className="block text-[15px] font-semibold">{c.label}</span>
                  <span className="block text-[13px] text-muted">{c.mandatory ? `${c.description} Always on, it’s about your money or security.` : c.description}</span>
                </span>
                <span className={cx('flex shrink-0', c.mandatory && 'opacity-50')}>
                  <SwitchTrack on={c.push} />
                </span>
              </button>
            ))}
          </div>
        );
      })}
      <Notice msg={msg} onClose={() => setMsg(null)} />
    </Card>
  );
}

type Filter = 'all' | 'unread' | 'account' | 'trading';
const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'account', label: 'Account' },
  { id: 'trading', label: 'Trading' },
];

function InboxCard() {
  const items = useNotifications();
  const unread = useUnreadCount();
  const hasMore = useHasOlderNotifications();
  const [filter, setFilter] = useState<Filter>('all');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const groups = byDay(items.filter((n) => inTab(n, filter)));
  const older = async () => {
    setBusy(true);
    setErr(null);
    try {
      await loadOlderNotifications();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <H2>Inbox</H2>
        <PillButton kind="secondary" size="sm" disabled={!unread} onClick={() => void markAllRead()}>
          Mark all as read
        </PillButton>
      </div>
      <div className="no-scrollbar mt-5 flex overflow-x-auto">
        <Segmented label="Show" options={FILTERS} value={filter} onChange={setFilter} className="shrink-0 max-sm:!flex max-sm:[&>button]:px-3 max-sm:[&>button]:text-[14px]" />
      </div>

      {groups.map((g) => (
        <div key={g.label}>
          <h3 className="m-0 mt-7 mb-1 text-[13px] font-semibold tracking-wide text-muted uppercase">{g.label}</h3>
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {g.items.map((n) => (
              <li key={n.id} className={cx('-mx-4 flex items-start gap-4 rounded-2xl px-4 py-5', !n.read && 'bg-fg/[0.035]')}>
                <NotificationIcon n={n} />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <div className="flex items-baseline gap-3">
                    <span className={cx('min-w-0 text-[16px] leading-snug', n.read ? 'font-medium' : 'font-semibold')}>{n.title}</span>
                    {!n.read && (
                      <span className="size-2 shrink-0 self-center rounded-full bg-fg">
                        <span className="sr-only">Unread</span>
                      </span>
                    )}
                    <span className="num ml-auto shrink-0 text-[13px] text-muted">{timeLabel(n.createdAt)}</span>
                  </div>
                  {n.body && <p className="m-0 line-clamp-3 text-[15px] leading-normal text-muted">{n.body}</p>}
                  {(n.link || !n.read) && (
                    <div className="mt-2.5 flex flex-wrap gap-2">
                      {n.link && (
                        <PillButton kind={isUrgent(n) ? 'primary' : 'secondary'} size="sm" onClick={() => openNotification(n)}>
                          {ctaLabel(n)}
                        </PillButton>
                      )}
                      {!n.read && (
                        <button type="button" onClick={() => void markRead([n.id])} className="h-10 rounded-full px-4 text-[14px] font-semibold text-muted hover:text-fg">
                          Mark as read
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}

      {!groups.length && (
        <div className="flex flex-col items-center gap-3 px-4 pt-14 pb-10 text-center">
          <span aria-hidden="true" className="grid size-12 place-items-center rounded-full bg-track ring-1 ring-line-strong ring-inset">
            <Glyph name="check" />
          </span>
          <div className="text-[18px] font-semibold">{filter === 'all' ? 'No notifications yet' : 'You’re all caught up'}</div>
          <div className="text-[15px] text-muted">Deposits, alerts and account updates will appear here.</div>
        </div>
      )}

      {err && <p className="m-0 mt-4 text-down">{err}</p>}
      {hasMore && (
        <div className="mt-6 flex justify-center">
          <PillButton kind="secondary" busy={busy} onClick={() => void older()}>
            Show older notifications
          </PillButton>
        </div>
      )}
    </Card>
  );
}

function AlertsCard() {
  return (
    <Card className="flex flex-col gap-4">
      <SideIcon name="chart" />
      <H2 className="!text-[22px]">Your own alerts</H2>
      <p className="m-0 text-[16px] leading-relaxed text-muted">Price, move and portfolio alerts you set up yourself, each with its own app, push and email choice.</p>
      <Link to="/account/alerts" className="flex h-10 w-max items-center rounded-full bg-pill px-5 text-[14px] font-semibold text-fg no-underline">
        Manage alerts
      </Link>
    </Card>
  );
}

export function NotificationsPage({ tab }: { tab: 'inbox' | 'settings' }) {
  const unread = useUnreadCount();
  const [prefs, setPrefs] = useState<PushPreferences | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(() => {
    api
      .get<PushPreferences>('/api/notifications/prefs')
      .then(setPrefs)
      .catch((e) => setErr(errMsg(e)));
  }, []);
  useEffect(load, [load]);
  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <H1>Notifications</H1>
        <Segmented
          label="Notifications"
          className="w-full sm:w-auto"
          options={[
            {
              id: 'inbox' as const,
              label: (
                <>
                  Inbox
                  {unread > 0 && <span className="num grid h-5 min-w-5 place-items-center rounded-full bg-fg px-1.5 text-[11px] text-bg">{unread > 99 ? '99+' : unread}</span>}
                </>
              ),
            },
            { id: 'settings' as const, label: 'Settings' },
          ]}
          value={tab}
          onChange={(t) => navigate(t === 'inbox' ? '/account/notifications' : '/account/notifications/settings')}
        />
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,500px)] 2xl:grid-cols-[minmax(0,1fr)_minmax(0,564px)]">
        {tab === 'inbox' ? (
          <InboxCard />
        ) : (
          <div className="flex flex-col gap-4">
            {err && <p className="m-0 text-down">{err}</p>}
            {prefs && prefs.categories.length > 0 && <CategoriesCard prefs={prefs} setPrefs={setPrefs} />}
          </div>
        )}
        <div className="flex flex-col gap-8">
          <DeviceCard prefs={prefs} reload={load} />
          <AlertsCard />
        </div>
      </div>
    </div>
  );
}
